<script setup>
import { ref, computed } from 'vue'
import { useMainStore } from '@/stores/main'
const mainStore = useMainStore()
import UserApi from '@/api/User.js'

import Utils from '@/lib/util.js'
import Card from '@/components/Card.vue'

let email = ref("")
let name = ref("")
let topic = ref("")
let message = ref("")

let is_submitting = ref(false)
let failed = ref(false)
let is_sent = ref(false)

async function send() {
  is_submitting.value = true

  let res = await UserApi.contact( email.value, name.value, topic.value, message.value )

  if (res.status == "failed") {
    failed.value = true
  }

  is_submitting.value = false
  is_sent.value = true
}

const email_valid = computed(() => {
  return email.value != '' && Utils.is_email(email.value)
})

const email_help = computed(() => {
  return email.value != '' && !email_valid.value
})

const name_valid = computed(() => {
  return name.value != '' && name.value.length >= 3
})

const name_help = computed(() => {
  return name.value != '' && !name_valid.value
})

const topic_valid = computed(() => {
  return topic.value != '' && topic.value.length >= 3
})

const topic_help = computed(() => {
  return topic.value != '' && !topic_valid.value
})

const message_valid = computed(() => {
  return message.value != '' && message.value.length >= 10 
})

const message_help = computed(() => {
  return message.value != '' && !message_valid.value
})

</script>

<template>
<div class="container">
  <div class="row">
    <div class="col-md-6">
        <h1 class="title">Contact Us</h1>
        <p class="mb-5">
Whether you have questions about our products, need support, or want to learn more, we're here to help. Use the form on the right or one of the emails below.
        </p>


        <h1 class="sub-title">Products & Services</h1>
        <p class="mb-5">
Curious about what sets us apart? Schedule a personalized demo to see how we can meet your intelligence requirements. Contact us at info@webscout.io, or use the form.
        </p>

        <h1 class="sub-title">Support</h1>
        <p class="mb-5">
Have a technical issue or need assistance with integrations? Submit a support request to support@webscout.io, and we'll get back to you as soon as possible.
        </p>


        <h1 class="sub-title">About us</h1>
        <p class="enlarged">
Based in Copenhagen, Denmark, Webscout is an independent cybersecurity company specializing in high-resolution IP and network intelligence to help identify, track, and prevent internet-enabled threats.
 </p>

    </div>

    <div class="col-md-6">

      <div v-if="failed">
        <div class="box bordered p-5">
          <div style="padding: 2rem;" >
            <div class="text-center mb-4">
               <font-awesome-icon style="font-size: 5rem; color: #ca5050;" :icon="['fas', 'circle-exclamation']"/>
            </div>

            <h3 class="title mb-4 text-center">Failed</h3>
            <div class="text-center d-flex column centered">
              <p style="max-width: 700px;">Sorry! Something went wrong. Please try again later, or send an email directly to <a href="mailto:help@webscout.io"><strong class="white">hello@webscout.io</strong></a> with your question</p>
            </div>
          </div>
        </div>
      </div>
      <div class="box bordered p-5" v-else-if="!is_sent">
<!--
        <h1 class="title">Contact Us</h1>
        <p class="mb-3">
Whether you have questions about our products, need support, or want to learn more about how we can help, our team is ready to assist.
        </p>
-->

        <form class="form form-floating" id="f" @submit.prevent="send">
            <!--<input class="search_input form-control" id="floating" ref="i" v-model="search_input" placeholder="Query (CTRL+K)">-->
          <div class="mb-3">
            <div class="form-floating">
                <input type="email" v-model="email" class="form-control dark" id="floatingInput" placeholder="name@example.com" :class="{invalid: email_help}">
                <label for="floatingInput">Email address</label>
            </div>
          </div>

          <div class="mb-3">
            <div class="form-floating">
                <input type="text" v-model="name" class="form-control dark" id="floatingInput" placeholder="name@example.com" :class="{invalid: name_help}">
                <label for="floatingInput">Name</label>
            </div>
            <div v-if="name_help" class="small ps-2 pe-2 mt-1">
              Should be atleast 3 alphanumeric characters long
            </div>
          </div>

          <div class="mb-3">
            <div class="form-floating">
                <input type="text" v-model="topic" class="form-control dark" id="floatingInput" placeholder="name@example.com" :class="{invalid: topic_help}">
                <label for="floatingInput">Topic</label>
            </div>
            <div v-if="topic_help" class="small ps-2 pe-2 mt-1">
              Should be atleast 3 alphanumeric characters long
            </div>
          </div>

          <div class="mb-3">
            <div class="form-floating mb-2">
              <textarea class="form-control dark" v-model="message" id="floatingPassword" placeholder="Password" style="height: 300px;" :class="{invalid: message_help}"></textarea>
              <label for="floatingInput">Message</label>
            </div>

            <div v-if="message_help" class="small ps-2 pe-2 mt-1">
              Should be atleast 10 alphanumeric characters long
            </div>
          </div>

          <button id="submit-btn" v-if="!is_submitting" :disabled="!email_valid || !name_valid || !message_valid || !topic_valid" type="submit" class="btn btn-primary">Send</button>
          <button id="submit-btn" v-else disabled type="submit" class="btn btn-primary submit-btn">Sending...</button>
         
        </form>
      </div>
      <Transition v-else>
      <div class="box bordered p-5">
        <div  style="padding: 2rem;">
          <div class="text-center mb-4">
            <img src="~@/assets/icons/check-circle-filled.svg" width="100" height="100" alt="">
          </div>

          <h3 class="title mb-4 text-center">Sent!</h3>
          <div class="text-center d-flex column centered">
            <p style="max-width: 700px;">We are happy to hear from you - we will get back to you within a day or two</p>
            <p>Thank you!</p>
          </div>
        </div>
      </div>
      </Transition>

    </div>
  </div>

</div>

</template>

<style scoped>
.title {
  font-size: 2rem !important;
  font-family: "Jura", sans-serif;
}


.sub-title {
  font-weight: bold;
  font-size: 1.5rem;
  display: inline-block;
  font-family: "Jura", sans-serif;
}

.solution {
  height: 350px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  background-image: linear-gradient(150deg, #1B2029 -30%, #181D26 110%);
  border-radius: 10px;

  h5 {
    margin: 0;
  }
}

.box-header {
  font-size: 1.3rem;
  color: white;
}

.integration {
  display: flex;
  align-items: center;

  margin-bottom: 1rem;
}

.integration .text {
  color: white;
  font-size: 1.1rem;
}

.integration .icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bfc4ce;
  padding: .8rem;
  border-radius: 7px;
  background-color: #2f3642;
  margin-right: 1rem;
}

.invalid {
  outline: 1px solid #ef8892; 
}

.button {
  display: flex;
  padding: .4rem 1.1rem;
  border-radius: 10px;
  background-color: #5094ed;
  color: white;
  align-items: center;
  border: none;
}

.button:hover {
  background-color: #4c82c8;
  cursor: pointer;
}

.button.black {
  background-color: #232A35;
}

.button.black:hover {
  background-color: #2F3744;
}

.enlarged {
  font-weight: 400;
  color: #7f8796;
}

#submit-btn {
    height: 64px;
    background-color: #2D3642;
    font-size: 1rem;
    outline:none;
    width: 100%;
    box-shadow: none;
    border:none;
    border-radius: 10px;
}

#submit-btn:hover {
    background-color: #36404F;
}

</style>

